// File: src/pages/Checkout.jsx
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { apiClient } from "../../api";
import { useProperty } from "../../Contexts/PropertyContext/PropertyContext";
import { baseUrl, stripePublicKey } from "../../Environments/environment";
import { toast } from "react-toastify";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import {
  DEFAULT_PRICING,
  formatPaymentAmount,
} from "./paymentCurrency.util";


const stripePromise = loadStripe(stripePublicKey);

/**
 * CheckoutContent renders inside Elements when clientSecret is available
 */
function CheckoutContent({ amountDisplay }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!stripe || !elements) {
      setLoading(false);
      return;
    }

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });

      // CASE 1: UI/frame/validation errors → don't navigate
      if (result.error) {
        console.error("Stripe UI error:", result.error);
        toast.error(result.error.message);
        setLoading(false);
        return;
      }

      const status = result.paymentIntent?.status;

      if (status === "succeeded") {
        navigate("/dashboard/success");
        return;
      }

      if (status === "processing") {
        toast.info(
          "Payment is processing. Reviews will be added once confirmed."
        );
        navigate("/dashboard/success");
        return;
      }

      navigate("/dashboard/payment-failed", {
        state: {
          error:
            status === "requires_action"
              ? "Additional authentication is required. Please try again."
              : "Payment was not completed. Please try again.",
        },
      });
    } catch (err) {
      // CASE 3: Critical system crash (Rare)
      console.error("Unexpected error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handlePageOneBack = () => {
    // Prefer real browser history so user returns to exact origin page.
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    // Safe fallback when opened directly without in-app history.
    navigate("/dashboard/add-credits");
  };
  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-md mx-auto p-4 sm:p-6 border rounded-md bg-white"
    >
       <div className="flex justify-between items-center mb-4">
    <h2 className="text-xl font-semibold ">Checkout</h2>
    <button
                type="button"
                onClick={handlePageOneBack}
                className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <ArrowLeftIcon className="h-4 w-4" aria-hidden />
                Back
              </button></div>  
              <hr className="my-4" />
      <div className="mb-4 text-sm text-gray-600">Enter payment details</div>
      <div className="md:min-w-[400px] min-w-0 ">
        <PaymentElement />
      </div>
      <button
        type="submit"
        disabled={!stripe || loading}
        className="mt-4 w-full px-4 py-2 bg-indigo-600 text-white rounded-md"
      >
        {loading ? "Processing..." : `Pay ${amountDisplay} & Save Card`}
      </button>
    </form>
  );
}

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [clientSecret, setClientSecret] = useState("");
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const amountParam = Number(searchParams.get("amount")) || 0;
  const signParam = String(searchParams.get("sign")) || "";
  const { selectedPropertyId } = useProperty();
  const propertyId = selectedPropertyId;

  const amountDisplay = formatPaymentAmount(amountParam, pricing.currency);

  const redirectToAddCredits = (message) => {
    toast.error(message);
    navigate("/dashboard/add-credits", { replace: true });
  };

  const validateAndCheckout = async () => {
    try {
      const response = await apiClient.get(
        `${baseUrl}/stripe/wallet/${propertyId}`,
      );
      const wallet = response.data.result;
      const walletPricing = wallet?.pricing ?? DEFAULT_PRICING;
      setPricing(walletPricing);

      if (amountParam < walletPricing.minSignedPurchaseCents) {
        redirectToAddCredits("Minimum purchase is 500 reviews.");
        return;
      }

      await apiClient.get(
        `${baseUrl}/stripe/verify-sign/${propertyId}?amount=${amountParam}&sign=${signParam}`,
      );
      await createIntent(wallet);
    } catch (error) {
      console.error(error);
      const errorMessage =
        error.response?.data?.error?.message ||
        "Invalid or expired checkout link.";
      redirectToAddCredits(errorMessage);
    }
  };

  const createIntent = async (wallet) => {
    try {
      const customerId = wallet?.stripeCustomerId;
      if (!customerId) {
        toast.error("No Stripe customer found. Create a customer first.");
        navigate("/dashboard/add-credits");
        return;
      }
      const response2 = await apiClient.post(
        `${baseUrl}/stripe/create-payment-intent`,
        { customerId, propertyId, userId: wallet._id, amount: amountParam },
      );
      const { clientSecret } = response2.data.result;
      setClientSecret(clientSecret);
    } catch (err) {
      console.error("Failed creating payment intent:", err);
      navigate("/dashboard/payment-failed", {
        state: { error: err.message },
      });
    }
  };
  useEffect(() => {
    if (!propertyId) {
      redirectToAddCredits("Select a property before checkout.");
      return;
    }

    if (!signParam || !amountParam) {
      redirectToAddCredits("Invalid checkout link. Please start again.");
      return;
    }

    validateAndCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountParam, signParam, propertyId]);

  if (!clientSecret) {
    return (
      <div className="p-4 sm:p-6">
        <div>Preparing payment...</div>
      </div>
    );
  }

  const options = {
    clientSecret,
    appearance: { theme: "stripe" },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <div className="p-4 sm:p-6">
      
   
        <CheckoutContent amountDisplay={amountDisplay} />
      </div>
    </Elements>
  );
}
